/**
 * Split Media Slider — split-media-slider.js
 * - Prev/next navigation
 * - Dot indicators with progress bar
 * - Auto-slide with configurable interval
 * - Pause on hover
 * - Touch/swipe support
 * - Re-triggers CSS animations on slide change
 */

class SplitMediaSlider {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('.slider__track');
    this.slides = [...el.querySelectorAll('.slide')];
    this.dots = [...el.querySelectorAll('.slider__dot')];
    this.btnPrev = el.querySelector('.slider__btn--prev');
    this.btnNext = el.querySelector('.slider__btn--next');

    this.current = 0;
    this.total = this.slides.length;
    this.autoSlide = el.dataset.autoSlide === 'true';
    this.interval = parseInt(el.dataset.autoSlideInterval, 10) || 5000;
    this.timer = null;

    if (this.total <= 1) {
      this.activateSlide(0);
      return;
    }

    this.init();
  }

  init() {
    this.activateSlide(0);

    this.btnPrev?.addEventListener('click', () => {
      this.go(this.current - 1);
      this.resetAutoSlide();
    });

    this.btnNext?.addEventListener('click', () => {
      this.go(this.current + 1);
      this.resetAutoSlide();
    });

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.go(i);
        this.resetAutoSlide();
      });
    });

    // Touch / swipe
    let startX = 0;
    this.track.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    this.track.addEventListener('touchend', (e) => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) {
        this.go(diff > 0 ? this.current + 1 : this.current - 1);
        this.resetAutoSlide();
      }
    }, { passive: true });

    // Pause on hover
    this.el.addEventListener('mouseenter', () => this.pauseAutoSlide());
    this.el.addEventListener('mouseleave', () => this.startAutoSlide());

    if (this.autoSlide) this.startAutoSlide();
  }

  go(index) {
    this.current = ((index % this.total) + this.total) % this.total;
    this.track.style.transform = `translateX(-${this.current * 100}%)`;
    this.activateSlide(this.current);
    this.updateDots(this.current);
  }

  activateSlide(index) {
    this.slides.forEach((slide, i) => {
      if (i === index) {
        slide.classList.add('is-active');
        // Re-trigger animations by re-cloning animated elements
        slide.querySelectorAll('.slide__anim:not(.slide__anim--none)').forEach((el) => {
          el.style.animationName = 'none';
          // Force reflow
          void el.offsetWidth;
          el.style.animationName = '';
        });
      } else {
        slide.classList.remove('is-active');
      }
    });
  }

  updateDots(index) {
    this.dots.forEach((dot, i) => {
      const progress = dot.querySelector('.slider__dot-progress');
      dot.classList.toggle('is-active', i === index);
      if (progress) {
        // Reset progress
        progress.style.transition = 'none';
        progress.style.width = '0%';
        void progress.offsetWidth;

        if (i === index && this.autoSlide) {
          progress.style.transition = `width ${this.interval}ms linear`;
          progress.style.width = '100%';
        }
      }
    });
  }

  startAutoSlide() {
    if (!this.autoSlide) return;
    this.timer = setInterval(() => {
      this.go(this.current + 1);
    }, this.interval);

    // Kick off progress on current dot
    this.updateDots(this.current);
  }

  pauseAutoSlide() {
    clearInterval(this.timer);
    this.timer = null;
    // Freeze dot progress
    this.dots.forEach((dot) => {
      const progress = dot.querySelector('.slider__dot-progress');
      if (progress && dot.classList.contains('is-active')) {
        const computed = getComputedStyle(progress).width;
        progress.style.transition = 'none';
        progress.style.width = computed;
      }
    });
  }

  resetAutoSlide() {
    this.pauseAutoSlide();
    if (this.autoSlide) this.startAutoSlide();
  }
}

// Init all sliders on page
document.querySelectorAll('.split-media-slider').forEach((el) => {
  new SplitMediaSlider(el);
});
